/*  Copyright 2018 - 2021 Swiss Federal Institute of Technology Lausanne (EPFL)
 *
 *  Licensed under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License.
 *  You may obtain a copy of the License at
 *
 *  http://www.apache.org/licenses/LICENSE-2.0.
 *
 *  Unless required by applicable law or agreed to in writing, software
 *  distributed under the License is distributed on an "AS IS" BASIS,
 *  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *  See the License for the specific language governing permissions and
 *  limitations under the License.
 *
 *  This open source software code was developed in part or in whole in the
 *  Human Brain Project, funded from the European Union's Horizon 2020
 *  Framework Programme for Research and Innovation under
 *  Specific Grant Agreements No. 720270, No. 785907, and No. 945539
 *  (Human Brain Project SGA1, SGA2 and SGA3).
 *
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *       http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 *
 */
import type API from './API';
import type { QuerySpecification } from '../Types/QuerySpecification';
import type { UUID, Stage, Settings, UserProfile, Space, Type, TypesByName, QueryExecutionResult, KGCoreResult } from '../types';

const RELATIVE_ROOT_PATH = '/static/data';

declare global {
	interface Window {
		rootPath?: string
	}
}

const getSize = (size?: string|null) => {
  if (size !== undefined && size !== null) {
    return `size=${size}&`;
  }
  return '';
};

const getFrom = (from?: string|null) => {
  if (from !== undefined && from !== null) {
    return `from=${from}&`;
  }
  return '';
};

const getInstanceId = (instanceId?: UUID|null) => {
  if (instanceId !== undefined && instanceId !== null) {
    return `instanceId=${encodeURIComponent(instanceId)}&`;
  }
  return '';
};

const getStage = (stage?: Stage) => {
  if (stage) {
    return `stage=${stage}`;
  }
  return '';
};

const endpoints = {
  settings: () => `${RELATIVE_ROOT_PATH}/settings.json`,
  user: () => `${RELATIVE_ROOT_PATH}/user.json`,
  spaces: () => `${RELATIVE_ROOT_PATH}/spaces.json`,
  types: () => `${RELATIVE_ROOT_PATH}/types.json`,
  performQuery: (stage?:Stage, from?:string, size?:string, instanceId?:string, restrictToSpaces?:string[], params?: object) => {
    const restrictToSpacesString =
      Array.isArray(restrictToSpaces) && restrictToSpaces.length
        ? '&restrictToSpaces=' +
          restrictToSpaces.map((space) => encodeURIComponent(space)).join(',')
        : '';
    const paramsString = params?Object.entries(params).reduce(
      (acc, [name, value]) => {
        acc += `&${encodeURIComponent(name)}=${encodeURIComponent(value as string)}`;
        return acc;
      },
      ''
    ):'';
    return `${RELATIVE_ROOT_PATH}/run.json?${getSize(size)}${getFrom(from)}${getInstanceId(instanceId)}${getStage(stage)}${paramsString}${restrictToSpacesString}`;
  },
  getQuery: (queryId:UUID) => `${RELATIVE_ROOT_PATH}/query.json?queryId=${queryId}`,
  getQueries: (type:string) => `${RELATIVE_ROOT_PATH}/queries.json?type=${encodeURIComponent(type)}`
};

const fetchData = async (url: string): Promise<unknown> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return await response.json();
};

class APIMockAdapter implements API {

  async getSettings(): Promise<Settings> {
    const data = await fetchData(endpoints.settings());
    return (data as { data: Settings})?.data;
  }

  async getUserProfile(): Promise<UserProfile> {
    const data = await fetchData(endpoints.user());
    return (data as { data: UserProfile})?.data ;
  }

  async getSpaces(): Promise<KGCoreResult<Space[]>> {
    const data = await fetchData(endpoints.spaces());
    return data as KGCoreResult<Space[]>;
  }

  async listTypes(): Promise<Type[]> {
    const data = await fetchData(endpoints.types());
    return data as Type[];
  }

  async getTypesByName(types: string[]): Promise<TypesByName> {
    console.log(`fetching types ${types}`);
    const data = await fetchData(endpoints.types());
    return (data as { data: TypesByName})?.data ;
  }

  async performQuery(
    query: QuerySpecification.QuerySpecification,
    stage: Stage,
    from: string,
    size: string,
    instanceId: UUID | undefined,
    restrictToSpaces: string[]| undefined,
    params: object
  ): Promise<QueryExecutionResult> {
    console.log('run query', query);

    const res = await fetchData(endpoints.performQuery(
      stage,
      from,
      size,
      instanceId,
      restrictToSpaces,
      params
    ));
    const data = res as QueryExecutionResult;
    data.from = Number(from);
    data.size = Number(size);
    data.total = Number(from) + Number(size) + 100;
    return data;
  }

  async getQueries(type: string): Promise<KGCoreResult<QuerySpecification.QuerySpecification[]>> {
    const data = await fetchData(endpoints.getQueries(type));
    return data as KGCoreResult<QuerySpecification.QuerySpecification[]>;
  }

  async getQuery(queryId: UUID): Promise<QuerySpecification.QuerySpecification> {
    const res = await fetchData(endpoints.getQuery(queryId));
    const data = res as QuerySpecification.QuerySpecification;
    data['@id'] = queryId;
    return data;
  }

  async saveQuery(
    queryId: UUID,
    query: QuerySpecification.QuerySpecification,
    space: string
  ): Promise<void> {
    console.log(`saving query ${queryId} in space ${space}`, query);
  }

  async deleteQuery(queryId: UUID): Promise<void> {
    console.log(`deleting uery ${queryId}`);
  }
}

export default APIMockAdapter;
